/**
 * The marks on a control chart's points, and its tooltip. A point that broke a rule is a red dot; a value at
 * the sensor's ceiling or floor (censored: plotted, never used for the limits) is a hollow triangle pointing at
 * the limit it sits on; a point before the baseline is a muted dot. With many points ("dense") only those
 * marks are drawn, not a dot on every reading.
 */
import type { SpcPlot, SpcPlotRow } from "./plot";
import { spcNum, spcWhen } from "./plot";
import { SERIES_PALETTE } from "../../series-palette";

const DANGER = "rgb(var(--color-danger))";
const WARNING = "rgb(var(--color-warning))";

export interface SpcDotProps {
  mode: "plain" | "dense" | "muted";
  // Set by Recharts when it clones the dot for each point.
  cx?: number | null;
  cy?: number | null;
  value?: unknown;
  payload?: SpcPlotRow;
}

export function SpcDot({ mode, cx, cy, value, payload }: SpcDotProps) {
  if (cx == null || cy == null || value == null || !payload) return null;
  if (payload.censored) {
    const up = payload.censored === "ceiling";
    const tip = up ? cy - 5 : cy + 5;
    const base = up ? cy + 3 : cy - 3;
    return (
      <path
        d={`M${cx},${tip} L${cx + 4.5},${base} L${cx - 4.5},${base} Z`}
        fill="rgb(var(--color-surface))"
        stroke={payload.flagged ? DANGER : WARNING}
        strokeWidth={1.5}
      />
    );
  }
  if (payload.flagged) {
    return <circle cx={cx} cy={cy} r={4} fill={DANGER} stroke="rgb(var(--color-surface))" strokeWidth={1} />;
  }
  if (mode === "dense") return null;
  return mode === "muted" ? (
    <circle cx={cx} cy={cy} r={2} fill="rgb(var(--color-on-surface-muted))" fillOpacity={0.55} />
  ) : (
    <circle cx={cx} cy={cy} r={2.4} fill={SERIES_PALETTE[0]} />
  );
}

const PHASE: Record<NonNullable<SpcPlotRow["phase"]>, string> = {
  pre: "Before the baseline",
  I: "In the baseline window: the limits come from these points",
  II: "Checked against the limits",
};

const GAP: Record<NonNullable<SpcPlotRow["gap"]>, string> = {
  stuck: "Stuck reading, treated as missing",
  no_data: "No readings here",
};

const CENSORED: Record<NonNullable<SpcPlotRow["censored"]>, string> = {
  ceiling: "At the sensor's upper limit: shown, not used for the limits",
  floor: "At the sensor's lower limit: shown, not used for the limits",
};

export interface SpcTooltipProps {
  kind: SpcPlot["kind"];
  unit?: string;
  // Set by Recharts.
  active?: boolean;
  payload?: ReadonlyArray<{ payload?: unknown }>;
}

export function SpcTooltip({ kind, unit, active, payload }: SpcTooltipProps) {
  const row = active ? (payload?.[0]?.payload as SpcPlotRow | undefined) : undefined;
  if (!row) return null;
  const u = unit ? ` ${unit}` : "";
  const value = row.v ?? row.vPre;
  let reading: string;
  if (value === null) reading = GAP[row.gap ?? "no_data"];
  else if (kind === "cusum") reading = `Upper sum ${spcNum(value)}, lower sum ${spcNum(row.lower ?? 0)}`;
  else if (kind === "ewma") reading = `Smoothed ${spcNum(value)}${u}`;
  else reading = `${spcNum(value)}${u}`;
  let limits: string | null = null;
  if (kind === "cusum" && row.ucl !== null) limits = `Signals above ${spcNum(row.ucl)}`;
  else if (row.center !== null && row.ucl !== null && row.lcl !== null) {
    limits = `Center ${spcNum(row.center)}, limits ${spcNum(row.lcl)} to ${spcNum(row.ucl)}`;
  }
  return (
    <div className="max-w-[18rem] space-y-0.5 rounded-md border border-border bg-surface-elevated px-2.5 py-1.5 text-xs text-on-surface shadow-md">
      <p className="font-medium">{row.at ? spcWhen(row.at) : `Point ${row.x}`}</p>
      <p>{reading}</p>
      {row.phase && <p className="text-on-surface-muted">{PHASE[row.phase]}</p>}
      {limits && <p className="text-on-surface-muted">{limits}</p>}
      {row.censored && <p className="text-warning">{CENSORED[row.censored]}</p>}
      {row.signals.map((s) => (
        <p key={s} className="text-danger">
          {s}
        </p>
      ))}
    </div>
  );
}
